import { useEffect, useRef, useState, type RefObject } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { tileImage } from '@/lib/modern'
import { morph } from '@/lib/motion'
import type { WallHandle } from './Wall'

// A photo opened from a wall as a post, kept in the address as ?photo= (or param) so Back closes it and a link opens it. It
// flies from its tile into the post, and back into its tile when the post closes.
export function useOpenPhoto(wall: RefObject<WallHandle | null>, param = 'photo') {
  const navigate = useNavigate()
  const location = useLocation()
  const [params, setParams] = useSearchParams()
  const wanted = params.get(param)
  const [openId, setOpenId] = useState(wanted)
  const quiet = useRef(false)
  // A photo changed here at once (inside a morph), while the address catches up: nothing to follow until it has.
  const settling = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (settling.current !== undefined) {
      if (wanted === settling.current) settling.current = undefined
      return
    }
    if (wanted === openId) return
    if (quiet.current) {
      quiet.current = false
      setOpenId(wanted)
    } else if (wanted && !openId) {
      const tile = tileImage(wanted)
      morph('open', () => setOpenId(wanted), {
        prepare: () => tile && (tile.style.viewTransitionName = 'mp-hero'),
        landed: () => tile && (tile.style.viewTransitionName = ''),
      })
    } else if (!wanted && openId) {
      let tile: HTMLImageElement | null = null
      morph('close', () => setOpenId(null), {
        landed: () => {
          tile = wall.current?.reveal(openId) ?? null
          if (tile) tile.style.viewTransitionName = 'mp-hero'
        },
        done: () => tile && (tile.style.viewTransitionName = ''),
      })
    } else morph('swap', () => setOpenId(wanted))
  }, [wanted, openId, wall])

  const withPhoto = (id: string | null) => {
    const q = new URLSearchParams(params)
    q.delete('ring')
    if (id) q.set(param, id)
    else q.delete(param)
    return q
  }
  const open = (id: string) => setParams(withPhoto(id), { state: { opened: true } })
  const close = () => {
    if ((location.state as { opened?: boolean } | null)?.opened) navigate(-1)
    else setParams(withPhoto(null), { replace: true })
  }
  const go = (id: string) => {
    quiet.current = true
    setParams(withPhoto(id), { replace: true, state: location.state })
  }
  const swap = (id: string, from: HTMLElement) => {
    const current = document.querySelector<HTMLElement>('.mp-hero')
    morph(
      'swap',
      () => {
        settling.current = id
        setOpenId(id)
        setParams(withPhoto(id), { replace: true, state: location.state })
      },
      {
        prepare: () => {
          if (current) current.style.viewTransitionName = 'none'
          from.style.viewTransitionName = 'mp-hero'
        },
        landed: () => (from.style.viewTransitionName = ''),
      },
    )
  }
  // The post put away at once, inside a morph the caller runs, while the address catches up.
  const putAway = () => {
    settling.current = null
    setOpenId(null)
  }
  return { openId, withPhoto, open, close, go, swap, putAway }
}
